import { uploadRecordingHttp, RecordingUploadMeta } from '../api/RecordingUploadService';

// How long to wait for the server to acknowledge `process_recording` — counted
// from when the browser's send buffer is empty — before assuming the socket is
// dead and falling back to the HTTP re-upload. Generous on purpose: an empty
// bufferedAmount only means the bytes reached the OS/network layer, and on a slow
// uplink they can still take a while to reach the server. Falling back too early
// would re-upload an answer the server is about to process (duplicate). A dead
// socket normally shows up sooner as close/error.
const PROCESS_ACK_TIMEOUT_MS = 25000;
// Treat the socket as dead if the queued upload makes no visible progress for
// this long (bufferedAmount drops in steps, so allow for coarse updates).
const DRAIN_STALL_MS = 30000;
const DRAIN_POLL_MS = 200;

// VI-900: progress for the candidate while a submit is in flight.
export interface SubmitStatus {
  phase: 'uploading' | 'saving';
  percent?: number; // 0-100, uploading only
}
type SubmitStatusListener = (status: SubmitStatus) => void;

export interface SubmittableTake {
  ws?: WebSocket | null; // live streaming socket, if this take was recorded in this session
  blob: Blob;            // complete local copy of the recording
}

// VI-431: send the explicit Submit signal and resolve only once the server
// acknowledges it — from then on the server finishes processing and saving the
// answer even if the connection drops. Rejects on `process_recording_rejected`, socket
// close/error, or timeout — the caller then falls back to HTTP.
const sendProcessRecording = (ws: WebSocket, onStatus?: SubmitStatusListener): Promise<void> =>
  new Promise((resolve, reject) => {
    let settled = false;
    let ackTimer: ReturnType<typeof setTimeout> | undefined;
    let drainPoll: ReturnType<typeof setInterval> | undefined;
    const cleanup = () => {
      if (ackTimer) clearTimeout(ackTimer);
      if (drainPoll) clearInterval(drainPoll);
      ws.removeEventListener('message', onMessage);
      ws.removeEventListener('close', onClose);
      ws.removeEventListener('error', onClose);
    };
    const settle = (ok: boolean, reason?: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (ok) resolve();
      else reject(new Error(reason));
    };
    const onMessage = (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      try {
        const parsed = JSON.parse(event.data);
        if (parsed.type === 'process_recording_ack') settle(true);
        else if (parsed.type === 'process_recording_rejected') settle(false, 'process_recording rejected by server');
      } catch {
        // not a control message we care about
      }
    };
    const onClose = () => settle(false, 'socket closed before process_recording was acknowledged');

    ws.addEventListener('message', onMessage);
    ws.addEventListener('close', onClose);
    ws.addEventListener('error', onClose);
    try {
      ws.send(JSON.stringify({ type: 'process_recording' }));
    } catch {
      settle(false, 'failed to send process_recording');
      return;
    }

    // The signal is queued behind any live-streamed chunks still in the send
    // buffer. On a slow uplink that backlog can take a while to drain; timing out
    // before the server could even read the signal would re-upload a take the
    // server is about to process (duplicate answer). So only start the ack timer
    // once the buffer is empty, and give up early only if the upload stalls.
    const initialBuffered = ws.bufferedAmount;
    let lastBuffered = initialBuffered;
    let lastProgressAt = Date.now();
    const startAckTimer = () => {
      onStatus?.({ phase: 'saving' });
      ackTimer = setTimeout(() => settle(false, 'process_recording acknowledgement timed out'), PROCESS_ACK_TIMEOUT_MS);
    };
    if (lastBuffered === 0) {
      startAckTimer();
    } else {
      onStatus?.({ phase: 'uploading', percent: 0 });
      drainPoll = setInterval(() => {
        const buffered = ws.bufferedAmount;
        if (buffered === 0) {
          clearInterval(drainPoll);
          drainPoll = undefined;
          startAckTimer();
        } else if (buffered < lastBuffered) {
          lastBuffered = buffered;
          lastProgressAt = Date.now();
          onStatus?.({ phase: 'uploading', percent: Math.round((1 - buffered / initialBuffered) * 100) });
        } else if (Date.now() - lastProgressAt > DRAIN_STALL_MS) {
          settle(false, 'upload stalled before process_recording was delivered');
        }
      }, DRAIN_POLL_MS);
    }
  });

// VI-368 (resume) + VI-431 (confirmed submit): submit the jobseeker's chosen take.
//
//  - Same session (the take's WebSocket is still open): send `process_recording`
//    on that socket and wait for the server's ack. The chunks were already
//    streamed live, so this is near-instant; once acked the server finishes the
//    pipeline even if the connection drops afterwards.
//
//  - No live socket (take restored from IndexedDB, socket dropped during review),
//    or the server rejected / never acked the signal: re-upload the complete
//    local blob over HTTP.
//
// Resolves only once the server has taken ownership of the answer (WS ack, or the
// HTTP endpoint's 202 after receiving the upload — it then saves in the
// background); rejects otherwise, so the caller must keep the candidate on the
// question (never advance on an unconfirmed submit).
export const submitTake = async (
  take: SubmittableTake,
  meta: RecordingUploadMeta,
  onStatus?: SubmitStatusListener,
): Promise<{ via: 'ws' | 'http' }> => {
  if (take.ws && take.ws.readyState === WebSocket.OPEN) {
    try {
      await sendProcessRecording(take.ws, onStatus);
      return { via: 'ws' };
    } catch (err) {
      console.warn('[VI-431] WebSocket submit not confirmed, falling back to HTTP upload', err);
      if (take.ws.readyState === WebSocket.OPEN) take.ws.close();
    }
  }
  onStatus?.({ phase: 'uploading', percent: 0 });
  await uploadRecordingHttp(take.blob, meta, fraction => {
    onStatus?.(fraction >= 1 ? { phase: 'saving' } : { phase: 'uploading', percent: Math.round(fraction * 100) });
  });
  return { via: 'http' };
};
